import React, { useState } from "react";
import {
	Tag,
	Button,
	Result,
	Skeleton,
	Space,
	Typography,
	Avatar,
	Drawer,
} from "antd";
import { connect, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import RenderSlot from "./RenderSlot";
import * as actions from "./actions";
import * as bookingActions from "../Booking/actions";
import { getAvailability } from "./reducers";
import { getAvailabilityRequests } from "../Booking/reducers";
import isEmpty from "lodash/isEmpty";
import compact from "lodash/compact";
import flatten from "lodash/flatten";
import {
	DeleteOutlined,
	UserOutlined,
	CalendarOutlined,
	ClockCircleOutlined,
	DollarOutlined,
	InfoCircleOutlined,
	RightOutlined,
} from "@ant-design/icons";
import { isDesktop, isMobile } from "react-device-detect";

const helperFindStartTime = (requests, reasonId, pId) =>
	requests.find(
		(rqst) => rqst.reasonId === reasonId && rqst.practitionerId === pId,
	);

function ServiceInfoCard({ reason, themeColor, onRemove }) {
	const [drawerVisible, setDrawerVisible] = useState(false);

	const hasPrice = reason.showTotalCost && (reason.onlineBookingsPrice > 0 || reason.price > 0);
	const price = reason.onlineBookingsPrice > 0 ? reason.onlineBookingsPrice : reason.price;

	return (
		<>
			{/* Clickable Service Card */}
			<div
				onClick={() => setDrawerVisible(true)}
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: '12px',
					background: '#fff',
					borderRadius: 8,
					border: '1px solid #e8e8e8',
					cursor: 'pointer',
					marginBottom: 12,
				}}
			>
				<div style={{ flex: 1, minWidth: 0 }}>
					<Typography.Text strong style={{ fontSize: 15, display: 'block' }}>
						{reason.name}
					</Typography.Text>
					<Space size={12} style={{ marginTop: 4 }}>
						{reason.minutes > 0 && (
							<Space size={4}>
								<ClockCircleOutlined style={{ color: '#888', fontSize: 12 }} />
								<Typography.Text type="secondary" style={{ fontSize: 12 }}>
									{reason.minutes} mins
								</Typography.Text>
							</Space>
						)}
						{hasPrice && (
							<Space size={4}>
								<DollarOutlined style={{ color: '#888', fontSize: 12 }} />
								<Typography.Text type="secondary" style={{ fontSize: 12 }}>
									${price}
								</Typography.Text>
							</Space>
						)}
					</Space>
				</div>
				<RightOutlined style={{ color: '#bbb', fontSize: 12 }} />
			</div>

			{/* Service Detail Drawer */}
			<Drawer
				title={null}
				placement="bottom"
				onClose={() => setDrawerVisible(false)}
				open={drawerVisible}
				height="auto"
				styles={{
					body: { padding: 0 },
					header: { display: 'none' },
					wrapper: isDesktop ? {
						maxWidth: 800,
						margin: '0 auto',
						boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
						borderRadius: '16px 16px 0 0',
					} : {},
				}}
				style={isDesktop ? { borderRadius: '16px 16px 0 0' } : {}}
			>
				<div style={{
					maxWidth: 800,
					margin: '0 auto',
					padding: '20px 24px',
				}}>
					{/* Close handle for mobile */}
					{isMobile && (
						<div style={{
							width: 40,
							height: 4,
							background: '#ddd',
							borderRadius: 2,
							margin: '0 auto 16px',
						}} />
					)}

					{/* Service Title */}
					<Typography.Title level={5} style={{ marginBottom: 16, marginTop: 0 }}>
						{reason.name}
					</Typography.Title>

					{/* Duration & Fee */}
					<div style={{ marginBottom: 16 }}>
						{reason.minutes > 0 && (
							<div style={{ marginBottom: 8 }}>
								<Space size={8}>
									<ClockCircleOutlined style={{ color: '#666' }} />
									<Typography.Text>
										<strong>Estimated Time:</strong> {reason.minutes} mins
									</Typography.Text>
								</Space>
							</div>
						)}
						{hasPrice && (
							<div>
								<Space size={8}>
									<DollarOutlined style={{ color: '#666' }} />
									<Typography.Text>
										<strong>Fee:</strong> ${price}
									</Typography.Text>
								</Space>
							</div>
						)}
					</div>

					{/* Description */}
					{reason.description && (
						<div style={{
							background: '#f5f5f5',
							padding: 12,
							borderRadius: 8,
							marginBottom: 16,
						}}>
							<Space align="start" size={8}>
								<InfoCircleOutlined style={{ color: '#666', marginTop: 2 }} />
								<div>
									<Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
										Information
									</Typography.Text>
									<Typography.Text type="secondary" style={{ fontSize: 13 }}>
										{reason.description}
									</Typography.Text>
								</div>
							</Space>
						</div>
					)}

					{/* Actions */}
					<div style={{ display: 'flex', gap: 12 }}>
						<Button
							block
							size="large"
							onClick={() => setDrawerVisible(false)}
							style={{ flex: 1 }}
						>
							Close
						</Button>
						<Button
							block
							size="large"
							danger
							icon={<DeleteOutlined />}
							onClick={() => {
								setDrawerVisible(false);
								onRemove();
							}}
							style={{ flex: 1 }}
						>
							Remove
						</Button>
					</div>
				</div>
			</Drawer>
		</>
	);
}

function RenderTime({
	date,
	collatedAvailabilityData,
	updateItem,
	availabilityRequests,
	practiceId,
	toggleItem,
	fetchProfessionalAvailabilityRequest,
	reservationRequest,
	themeColor,
	deleteReservationRequest,
}) {
	const history = useHistory();

	let matchingRequest;

	// Find the requests whose startTime is selected
	const requestSlotTimeSelected = availabilityRequests.filter(
		(request) => request.startTime,
	);

	const loading = useSelector(
		(state) => state.availability.availabilityRangeLoadingState,
	);

	// match those with collated data, if already selected hide the service with other practitioners.
	collatedAvailabilityData = collatedAvailabilityData.filter(
		({ practitioner, reason }) => {
			const _matching = requestSlotTimeSelected.find(
				({ reasonId, startTime }) => reasonId === reason.id && startTime,
			);
			if (_matching) {
				return _matching.practitionerId === practitioner.id ? true : false;
			}
			return true;
		},
	);

	const hasSlotSelected = React.useCallback(
		(rsnId, ppId) => {
			return availabilityRequests.find(
				({ reasonId, practitionerId, startTime }) =>
					reasonId === rsnId && practitionerId === ppId && startTime,
			);
		},
		[availabilityRequests],
	);

	const handleClear = React.useCallback(
		(reasonId, practitionerId) => {
			toggleItem({
				reasonId: reasonId,
				practitionerId: practitionerId,
			});
			fetchProfessionalAvailabilityRequest(practiceId);

			history.goBack();
		},
		[fetchProfessionalAvailabilityRequest, history, practiceId, toggleItem],
	);

	// Group availability data by reason (service) so we show the service card only once
	// This must be called before early returns to follow React hooks rules
	const groupedByReason = React.useMemo(() => {
		const groups = {};
		collatedAvailabilityData?.forEach((item) => {
			const reasonId = item.reason.id;
			if (!groups[reasonId]) {
				groups[reasonId] = {
					reason: item.reason,
					practitioners: [],
				};
			}
			groups[reasonId].practitioners.push({
				practitioner: item.practitioner,
				availability: item.availability,
				index: item.index,
			});
		});
		return Object.values(groups);
	}, [collatedAvailabilityData]);

	if (loading)
		return (
			<div>
				{[1, 2].map((i) => (
					<div key={i} style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
						<div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
							<Skeleton.Avatar active size={40} />
							<div>
								<Skeleton.Input active size="small" style={{ width: 140, marginBottom: 4 }} />
								<Skeleton.Input active size="small" style={{ width: 80, height: 12 }} />
							</div>
						</div>
						<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
							{[1, 2, 3, 4, 5, 6].map((j) => (
								<Skeleton.Button key={j} active shape="round" size="small" style={{ width: 70 }} />
							))}
						</div>
					</div>
				))}
			</div>
		);

	const allAvailabilites = compact(
		flatten(collatedAvailabilityData.map((d) => d.availability)),
	).filter((d) => !isEmpty(d));

	if (isEmpty(allAvailabilites) && !loading)
		return (
			<Result
				icon={<CalendarOutlined style={{ color: '#bfbfbf' }} />}
				title={<Typography.Text type="secondary" style={{ fontSize: 16 }}>No available times</Typography.Text>}
				subTitle={<Typography.Text type="secondary" style={{ fontSize: 14 }}>Try selecting a different date above</Typography.Text>}
			/>
		);

	return (
		<div>
			{groupedByReason.map(({ reason, practitioners }) => {
				// Check if any practitioner has availability for this reason
				const hasAnyAvailability = practitioners.some(
					({ availability }) => !isEmpty(flatten(availability))
				);

				if (!hasAnyAvailability) {
					return null;
				}

				// Find any practitioner ID for the remove action (we'll remove the whole service)
				const firstPractitionerId = practitioners[0]?.practitioner?.id;

				return (
					<div
						key={reason.id}
						style={{
							paddingBottom: 16,
							marginBottom: 16,
							borderBottom: "1px solid #f0f0f0",
						}}
					>
						{/* Service Info Card - shown once per service */}
						<ServiceInfoCard
							reason={reason}
							themeColor={themeColor}
							onRemove={() => handleClear(reason.id, firstPractitionerId)}
						/>

						{/* Practitioners for this service */}
						{practitioners.map(({ practitioner, availability, index }) => {
							matchingRequest = helperFindStartTime(
								availabilityRequests,
								reason.id,
								practitioner.id,
							);
							const slotSelected = hasSlotSelected(
								reason.id,
								practitioner.id,
							);

							if (isEmpty(flatten(availability))) {
								return null;
							}

							return (
								<div
									key={`${reason.id}-${practitioner.id}`}
									style={{
										padding: '12px',
										background: '#fafafa',
										borderRadius: 8,
										border: '1px solid #f0f0f0',
										marginBottom: 8,
									}}
								>
									{/* Practitioner Header */}
									<div style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										marginBottom: !slotSelected && availability && availability.length > 0 ? 12 : 0,
									}}>
										<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
											{practitioner.photo ? (
												<Avatar size={40} src={practitioner.photo}>
													{practitioner.firstName}
												</Avatar>
											) : (
												<Avatar
													size={40}
													style={{
														background: '#e8e8e8',
														color: '#666',
													}}
												>
													<UserOutlined />
												</Avatar>
											)}
											<div>
												<Typography.Text strong style={{ display: 'block', fontSize: 14 }}>
													{practitioner.fullNameWithTitle}
												</Typography.Text>
												{practitioner?.qualifications && (
													<Typography.Text type="secondary" style={{ fontSize: 12 }}>
														{practitioner.qualifications}
													</Typography.Text>
												)}
											</div>
										</div>

										<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
											{slotSelected && (
												<Tag color="green">Selected</Tag>
											)}

											{isDesktop && (
												<Button
													icon={<DeleteOutlined />}
													danger
													size="small"
													type="text"
													onClick={() => handleClear(reason.id, practitioner.id)}
												/>
											)}
										</div>
									</div>

									{/* Time Slots inside practitioner card */}
									{!slotSelected && availability && availability.length > 0 && (
										<div style={{
											borderTop: '1px solid #e8e8e8',
											paddingTop: 12,
											marginTop: 4,
										}}>
											<RenderSlot
												themeColor={themeColor}
												label="Morning"
												availability={availability?.[0]}
												updateItem={updateItem}
												practitioner={practitioner}
												reason={reason}
												matchingRequest={matchingRequest}
												practiceId={practiceId}
												reservationRequest={reservationRequest}
												deleteReservationRequest={deleteReservationRequest}
												date={date}
											/>
											<RenderSlot
												themeColor={themeColor}
												label="Afternoon"
												availability={availability?.[1]}
												updateItem={updateItem}
												practitioner={practitioner}
												reason={reason}
												matchingRequest={matchingRequest}
												practiceId={practiceId}
												reservationRequest={reservationRequest}
												deleteReservationRequest={deleteReservationRequest}
												date={date}
											/>
										</div>
									)}
								</div>
							);
						})}
					</div>
				);
			})}
		</div>
	);
}

const mapStateToProps = (state) => ({
	collatedAvailabilityData: getAvailability(state.availability),
	availabilityRequests: getAvailabilityRequests(state.booking),
});
export default connect(mapStateToProps, { ...actions, ...bookingActions })(
	RenderTime,
);
